import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { BrandProvider } from "./context/BrandContext";
import ScrollToTop from "./components/ScrollToTop";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import LoansPage from "./pages/LoansPage";
import PaymentsPage from "./pages/PaymentsPage";
import PaymentPage from "./pages/PaymentPage";
// import CashfreePayPage from "./pages/CashfreePayPage"; // Pay page disabled for now
import DocumentsPage from "./pages/DocumentsPage";
import SupportPage from "./pages/SupportPage";
import ProfilePage from "./pages/ProfilePage";
import KycPage from "./pages/KycPage";
import DigilockerClosePage from "./pages/DigilockerClosePage";
import ApplyLoanPage from "./pages/ApplyLoanPage";
import AAPage from "./pages/AAPage";
import AdminLoansPage from "./pages/AdminLoansPage";
import AdminDocumentsPage from "./pages/AdminDocumentsPage";
import DashboardLayout from "./components/DashboardLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import ESignPage from "./pages/ESignPage";
import SanctionLetterPage from "./pages/SanctionLetterPage";
import "./SafeTensorTheme.css";
import { useCallback, useEffect, useState } from "react";

const stripHtml = (text) =>
  String(text || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const friendlyErrorMessage = (status, rawMessage) => {
  const normalized = stripHtml(rawMessage);
  const lower = normalized.toLowerCase();

  if (!normalized) {
    return status >= 500
      ? "Our server is temporarily unavailable. Please try again in a moment."
      : `Request failed with status ${status}`;
  }

  if (
    lower.includes("bad gateway") ||
    lower.includes("gateway timeout") ||
    lower.includes("service unavailable") ||
    lower.includes("nginx")
  ) {
    return "Server is temporarily unavailable. Please try again shortly.";
  }

  return normalized;
};

function App() {
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [userSession, setUserSession] = useState(() => {
    const storedSession = localStorage.getItem("userSession");

    if (!storedSession) {
      return null;
    }

    try {
      return JSON.parse(storedSession);
    } catch (error) {
      console.error(
        "Failed to parse userSession from localStorage:",
        error,
      );

      return null;
    }
  });

  // ============================================================
  // USER SESSION LISTENER
  // ============================================================
  useEffect(() => {
    const handleUserSessionChange = () => {
      const storedSession =
        localStorage.getItem("userSession");

      if (!storedSession) {
        console.log(
          "userSession data in App.js: null",
        );

        setUserSession(null);
        return;
      }

      try {
        const parsedSession =
          JSON.parse(storedSession);

        console.log(
          "userSession data changed in App.js:",
          parsedSession,
        );

        setUserSession(parsedSession);
      } catch (error) {
        console.error(
          "Failed to parse updated userSession:",
          error,
        );

        setUserSession(null);
      }
    };

    // Log the initial session
    handleUserSessionChange();

    // Listen for changes in the same tab
    window.addEventListener(
      "userSessionChanged",
      handleUserSessionChange,
    );

    // Listen for changes from other browser tabs
    window.addEventListener(
      "storage",
      handleUserSessionChange,
    );

    return () => {
      window.removeEventListener(
        "userSessionChanged",
        handleUserSessionChange,
      );

      window.removeEventListener(
        "storage",
        handleUserSessionChange,
      );
    };
  }, []);

  // ============================================================
  // LOG WHEN REACT STATE CHANGES
  // ============================================================
  useEffect(() => {
    console.log(
      "Current userSession state:",
      userSession,
    );
  }, [userSession]);

  // ============================================================
  // OPTIONAL CRIF FUNCTION
  // ============================================================
  const fetchCrif = useCallback(async () => {
    console.log("🌐 [CRIF] Calling GET /api/crif/report");
    const response = await fetch(`${API_URL}/api/crif/report`, {
      method: "GET",
      credentials: "include",
      headers: userSession?.token
        ? { Authorization: `Bearer ${userSession.token}` }
        : undefined,
    });
    console.log("🌐 [CRIF] Response status:", response.status);

    const responseText = await response.text();
    let responseData = {};

    try {
      responseData = responseText ? JSON.parse(responseText) : {};
    } catch {
      responseData = { message: responseText };
    }

    if (!response.ok) {
      throw new Error(
        friendlyErrorMessage(response.status, responseData.message),
      );
    }

    return { ok: true, data: responseData };
  }, [API_URL, userSession?.token]);

  useEffect(() => {
    if (!userSession?.userId) return;

    let cancelled = false;

    window.dispatchEvent(new CustomEvent("crifReportLoading"));

    fetchCrif()
      .then(({ data }) => {
        console.log("CRIF report fetch successful:", data);
        if (!cancelled) {
          console.log("CRIF report fetched:", data);
          const reportData = data?.data || {};
          sessionStorage.setItem("crifReport", JSON.stringify(reportData));
          window.dispatchEvent(
            new CustomEvent("crifReportUpdated", { detail: reportData }),
          );
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("CRIF report fetch failed:", error);
          window.dispatchEvent(
            new CustomEvent("crifReportFailed", { detail: error.message }),
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchCrif, userSession?.userId]);

  return (
    <BrandProvider>
      <BrowserRouter>
        <ScrollToTop />

        <Routes>
          {/* Main website routes */}
          <Route
            index
            element={<Navigate to="/login" replace />}
          />

          <Route
            path="aa"
            element={<AAPage />}
          />

          <Route
            path="digilocker/close"
            element={<DigilockerClosePage />}
          />

          <Route
            path="login"
            element={<LoginPage />}
          />

          <Route
            path="forgot-password"
            element={<ForgotPasswordPage />}
          />

          <Route
            path="esign/:token"
            element={<ESignPage />}
          />

          {/* Dashboard routes - Protected */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="dashboard"
              element={<DashboardPage />}
            />

            <Route
              path="dashboard/loans"
              element={<LoansPage />}
            />

            <Route
              path="dashboard/payments"
              element={<PaymentsPage />}
            />

            <Route
              path="dashboard/make-payment"
              element={<PaymentPage />}
            />

            {/* Pay page disabled for now.
            <Route
              path="dashboard/pay"
              element={<CashfreePayPage />}
            />
            */}

            <Route
              path="dashboard/documents"
              element={<DocumentsPage />}
            />

            <Route
              path="dashboard/sanction-letter"
              element={<SanctionLetterPage />}
            />

            <Route
              path="dashboard/support"
              element={<SupportPage />}
            />

            <Route
              path="dashboard/profile"
              element={<ProfilePage />}
            />

            <Route
              path="dashboard/kyc"
              element={<KycPage />}
            />

            <Route
              path="dashboard/apply-loan"
              element={<ApplyLoanPage />}
            />

            <Route
              path="dashboard/admin/loans"
              element={<AdminLoansPage />}
            />

            <Route
              path="dashboard/admin/documents"
              element={<AdminDocumentsPage />}
            />
          </Route>

          {/* Unknown routes */}
          <Route
            path="*"
            element={<Navigate to="/login" replace />}
          />
        </Routes>
      </BrowserRouter>
    </BrandProvider>
  );
}

export default App;
